import { NavLink } from 'react-router-dom';
import {
  Shield,
  Users,
  UserMinus,
  Building2,
  ChevronLeft,
  ChevronRight,
  Settings,
  HelpCircle,
  Key,
  Bot,
  GraduationCap,
  MapPin,
  Plug,
  KeySquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
const WhaleIcon = '/whale-logo.png';

interface AdminSidebarProps {
  className?: string;
  style?: React.CSSProperties;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function AdminSidebar({
  className,
  style,
  isCollapsed,
  onToggleCollapse,
}: AdminSidebarProps) {
  // 菜单分组数据
  const menuGroups = [
    {
      title: '主菜单',
      items: [
        {
          id: 'organization-management',
          label: '租户管理',
          icon: Building2,
          path: '/admin/organization-management',
          disabled: false,
        },
      ],
    },
    {
      title: '系统配置',
      items: [
        {
          id: 'user-management',
          label: '用户管理',
          icon: Users,
          path: '/admin/user-management',
          disabled: false,
        },
        {
          id: 'backend-management',
          label: '后台管理',
          icon: Shield,
          path: '/admin/backend-management',
          disabled: false,
        },
        {
          id: 'remove-user-management',
          label: '移除用户',
          icon: UserMinus,
          path: '/admin/remove-user-management',
          disabled: false,
        },
        {
          id: 'authentication-management',
          label: '认证登录',
          icon: Key,
          path: '/admin/authentication-management',
          disabled: false,
        },
        {
          id: 'model-management',
          label: '模型管理',
          icon: Bot,
          path: '/admin/model-management',
          disabled: false,
        },
        {
          id: 'plugin-management',
          label: '数据对接',
          icon: Plug,
          path: '/admin/plugin-management',
          disabled: false,
        },
        {
          id: 'api-management',
          label: 'API管理',
          icon: KeySquare,
          path: '/admin/api-management',
          disabled: false,
        },
        {
          id: 'university-management',
          label: '高校信息',
          icon: GraduationCap,
          path: '/admin/university-management',
          disabled: false,
        },
        {
          id: 'administrative-division',
          label: '行政区域',
          icon: MapPin,
          path: '/admin/administrative-division',
          disabled: false,
        },
        {
          id: 'permission-management',
          label: '权限管理',
          icon: Shield,
          path: '/admin/permission-management',
          disabled: true,
        },
      ],
    },
    {
      title: '帮助中心',
      items: [
        {
          id: 'help',
          label: '使用帮助',
          icon: HelpCircle,
          path: '/admin/help',
          disabled: true,
        },
        {
          id: 'settings',
          label: '系统设置',
          icon: Settings,
          path: '/admin/settings',
          disabled: true,
        },
      ],
    },
  ];

  return (
    <aside
      className={cn(
        'flex flex-col bg-white border-r border-gray-200',
        isCollapsed ? 'w-20' : 'w-60',
        className
      )}
      style={style}
    >
      {/* Logo */}
      <div
        className={cn(
          'flex h-16 items-center border-b transition-all duration-300',
          isCollapsed ? 'justify-center px-3' : 'justify-between gap-2 px-6'
        )}
      >
        {isCollapsed ? (
          // 收起状态：只显示Logo图标
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
            }}
          >
            <img src={WhaleIcon} alt="WhaleHire" className="w-5 h-5" />
          </div>
        ) : (
          // 展开状态：显示Logo和标题
          <div className="flex items-center gap-3">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
              }}
            >
              <img src={WhaleIcon} alt="WhaleHire" className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">WhaleHire</h1>
              <p className="text-xs text-gray-500">管理后台</p>
            </div>
          </div>
        )}
      </div>

      {/* 菜单列表 */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="space-y-4">
          {menuGroups.map((group, groupIndex) => (
            <div key={group.title}>
              {/* 分组标题和收起按钮 - 仅展开状态显示 */}
              {!isCollapsed && (
                <div className="flex items-center justify-between px-3 mb-2">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {group.title}
                  </h3>
                  {/* 主菜单分组时显示收起按钮 */}
                  {group.title === '主菜单' && (
                    <button
                      onClick={onToggleCollapse}
                      className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:border-primary/50 transition-all"
                      title="收起侧边栏"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}

              {/* 收起状态下的展开按钮 - 显示在主菜单项前 */}
              {isCollapsed && group.title === '主菜单' && (
                <div className="mb-3 flex justify-center">
                  <button
                    onClick={onToggleCollapse}
                    className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:border-primary/50 transition-all"
                    title="展开侧边栏"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* 菜单项 */}
              <ul className="px-3 space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      {item.disabled ? (
                        // 禁用的菜单项
                        <div
                          className={cn(
                            'flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-400 cursor-not-allowed opacity-60',
                            isCollapsed && 'justify-center px-2'
                          )}
                          title={isCollapsed ? item.label : undefined}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          {!isCollapsed && <span>{item.label}</span>}
                        </div>
                      ) : (
                        // 可点击的菜单项
                        <NavLink
                          to={item.path}
                          className={({ isActive }) =>
                            cn(
                              'sidebar-nav-item transition-all duration-200 ease-in-out hover:translate-x-1',
                              isActive && 'active',
                              isCollapsed && 'justify-center px-2'
                            )
                          }
                          title={isCollapsed ? item.label : undefined}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          {!isCollapsed && <span>{item.label}</span>}
                        </NavLink>
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* 分组分隔线 - 不是最后一个组时显示 */}
              {!isCollapsed && groupIndex < menuGroups.length - 1 && (
                <div className="border-t border-gray-100 mt-4 pt-4"></div>
              )}
            </div>
          ))}
        </div>
      </nav>
    </aside>
  );
}
